import { action, computed, makeObservable, observable, runInAction } from "mobx"

import type { ManualClGraphQlTransport } from "@/domains/thick-v1/manual-cl-graphql-transport"
import type { ManualClPoolOhlcPoint } from "@/domains/validators"

export type OhlcWindow = "24h" | "7d" | "30d"

const WINDOW_SECONDS: Record<OhlcWindow, number> = {
  "24h": 24 * 60 * 60,
  "7d": 7 * 24 * 60 * 60,
  "30d": 30 * 24 * 60 * 60,
}

/**
 * Observable per-pool OHLC store. Backs the pool detail page's price / volume
 * chart. Loads on demand rather than polling — a user looking at a chart
 * usually switches tabs before a refresh would matter, and the hourly-
 * granularity data does not change within the typical session.
 *
 * Changing `window` refetches; switching pools clears. All values live here;
 * the view is a pure SVG renderer.
 */
export class PoolOhlcStore {
  points: Array<ManualClPoolOhlcPoint> = []
  activePoolAddress: string | undefined = undefined
  window: OhlcWindow = "7d"
  isLoading: boolean = false
  error: string | undefined = undefined

  constructor(private readonly transport: ManualClGraphQlTransport) {
    makeObservable(this, {
      points: observable,
      activePoolAddress: observable,
      window: observable,
      isLoading: observable,
      error: observable,
      setActivePool: action,
      setWindow: action,
      reload: action,
      latestClose: computed,
      priceDeltaPct: computed,
      windowPriceRange: computed,
    })
  }

  setActivePool(address: string | undefined): void {
    const next = address?.toLowerCase()
    if (next === this.activePoolAddress) return
    this.activePoolAddress = next
    this.points = []
    this.error = undefined
    if (next) void this.fetch()
  }

  setWindow(window: OhlcWindow): void {
    if (window === this.window) return
    this.window = window
    void this.fetch()
  }

  reload(): void {
    void this.fetch()
  }

  /** Most recent close price (token1 per token0). `undefined` when no data. */
  get latestClose(): number | undefined {
    const last = this.points[this.points.length - 1]
    return last?.close
  }

  /**
   * Percentage change between the first and last close in the current
   * window. Returns `undefined` when we do not have at least two points.
   */
  get priceDeltaPct(): number | undefined {
    if (this.points.length < 2) return undefined
    const first = this.points[0]?.open ?? this.points[0]?.close
    const last = this.latestClose
    if (first === undefined || last === undefined || first === 0) return undefined
    return ((last - first) / first) * 100
  }

  /**
   * Min/max price across the currently-loaded window, in whichever orientation
   * the upstream indexer stored (callers typically reconcile against their
   * canonical-orientation reference price — see `detectOrientation` in the
   * price-history composite). Falls back to `close` when `low`/`high` are
   * absent; returns `undefined` when we have no usable samples at all.
   *
   * Consumed by the range-brush to draw dashed reference lines at the
   * historical extremes, so the user can eyeball where their selection
   * sits relative to where the price has actually travelled.
   */
  get windowPriceRange(): { low: number; high: number } | undefined {
    if (this.points.length === 0) return undefined
    let low = Infinity
    let high = -Infinity
    for (const point of this.points) {
      const lowCandidate = point.low ?? point.close
      const highCandidate = point.high ?? point.close
      if (lowCandidate !== undefined && Number.isFinite(lowCandidate) && lowCandidate > 0) {
        if (lowCandidate < low) low = lowCandidate
      }
      if (highCandidate !== undefined && Number.isFinite(highCandidate) && highCandidate > 0) {
        if (highCandidate > high) high = highCandidate
      }
    }
    if (!Number.isFinite(low) || !Number.isFinite(high)) return undefined
    if (low > high) return undefined
    return { low, high }
  }

  private async fetch(): Promise<void> {
    const pool = this.activePoolAddress
    if (!pool) return
    const sinceUnix = Math.floor(Date.now() / 1000) - WINDOW_SECONDS[this.window]
    const requestedWindow = this.window
    runInAction(() => {
      this.isLoading = true
      this.error = undefined
    })
    try {
      const response = await this.transport.loadPoolOhlc(pool, sinceUnix)
      if (this.activePoolAddress !== pool || this.window !== requestedWindow) return
      runInAction(() => {
        this.points = response.points
        this.isLoading = false
      })
    } catch (error) {
      runInAction(() => {
        this.isLoading = false
        this.error = error instanceof Error ? error.message : "Failed to load OHLC"
      })
    }
  }
}
